import { setCurrentTask, setModalType, setTaskDetailResMsg } from '../actions/taskDetailActions';
import baseApi, { TAGS } from './baseApi';
import { sentryCaptureException } from '../utils/sentryUtils';
import { isEmpty, isNil } from 'lodash-es';
import { setTaskListErrorMessage } from '../actions/taskListActions';
import qs from 'query-string';
import { SSN_REQUIRED_TYPES } from '../constants';
import history from '../keeperHistory';
import { TASKS_PER_PAGE } from '../global/Environment';

////////////////////////////////////////// UTILS //////////////////////////////////////////
const formatGetTasksParams = ({ year, showAllReturns, order, filters, pageNumber, pageSize }) => {
  const params = new URLSearchParams();

  params.set('year', year);
  params.set('showAllReturns', showAllReturns ? '1' : '0');

  if (!isNil(pageNumber) && !isNil(pageSize)) {
    params.set('pageNumber', pageNumber);
    params.set('pageSize', pageSize);
  }
  if (!isNil(filters.search)) {
    params.set('searchTerm', filters.search);
  }
  if (!isEmpty(filters.assignee)) {
    params.set('assignee', filters.assignee);
  }
  if (!isNil(filters.substage)) {
    params.set('substage', filters.substage);
  }
  if (!isNil(order)) {
    params.append('order[]', order[0]); // column
    params.append('order[]', order[1]); // direction
  }
  if (!isEmpty(filters.stages)) {
    filters.stages.forEach((stage) => params.append('stages[]', stage));
  }

  return params;
};

/**
 * Handles error from an API call on the task list page.
 * @param {Error|object} e - The error object (can be an instance of Error or an object from the API)
 */
const handleTaskListError = (e) => (dispatch) => {
  let error = 'Unknown error. Please try again.';

  // If it's a client-side error, log to Sentry and set the error message.
  if (e instanceof Error) {
    sentryCaptureException(e);
    error = e.message;
  }
  // If it's a server-side error, just set the error message.
  else if (e?.error?.data?.msg) {
    error = e.error.data.msg;
  }

  dispatch(setTaskListErrorMessage(error));
};

/**
 * Handles error from an API call on the task detail page.
 * @param {Error|object} e - The error object (can be an instance of Error or an object from the API)
 * @param {string} title - The title to use on the error modal
 */
const handleTaskDetailError = (e, title) => (dispatch) => {
  let error = 'Error occurred';

  // If it's a client-side error, log to Sentry and set the error message.
  if (e instanceof Error) {
    sentryCaptureException(e);
    error = e.message;
  }
  // If it's a server-side error, just set the error message.
  else if (e?.error?.data?.msg) {
    error = e.error.data.msg;
  }

  dispatch(
    setTaskDetailResMsg({
      error,
      title
    })
  );
};

////////////////////////////////////////// API //////////////////////////////////////////
export const opsSystemApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    updateAssignee: builder.mutation({
      query: ({ taskId, assignee }) => ({
        url: 'ops-system/update-assignee',
        method: 'POST',
        body: { taskId, assignee }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error assigning task'));
        }
      }
    }),
    updateOpsReview: builder.mutation({
      query: ({ taskId, reviewer }) => ({
        url: 'ops-system/update-ops-review',
        method: 'POST',
        body: { taskId, reviewer }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateConfirmAmounts: builder.mutation({
      query: ({ taskId, sendNotification }) => ({
        url: 'ops-system/update-confirm-amounts',
        method: 'POST',
        body: { taskId, sendNotification }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateFinalReview: builder.mutation({
      query: ({ taskId, reviewer }) => ({
        url: 'ops-system/update-final-review',
        method: 'POST',
        body: { taskId, reviewer }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateFiledWithIrs: builder.mutation({
      query: ({ taskId }) => ({
        url: 'ops-system/update-filed-with-irs',
        method: 'POST',
        body: { taskId }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateAcceptedByIrs: builder.mutation({
      query: ({ taskId, reviewer }) => ({
        url: 'ops-system/update-accepted-by-irs',
        method: 'POST',
        body: { taskId, reviewer }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateNotStarted: builder.mutation({
      query: ({ taskId }) => ({
        url: 'ops-system/update-not-started',
        method: 'POST',
        body: { taskId }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateTaxExpertEsc: builder.mutation({
      query: ({ taskId }) => ({
        url: 'ops-system/update-tax-expert-esc',
        method: 'POST',
        body: { taskId }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateEngEsc: builder.mutation({
      query: ({ taskId, reviewer }) => ({
        url: 'ops-system/update-eng-esc',
        method: 'POST',
        body: { taskId, reviewer, origin: 'Moved to Eng Esc via QDeck' }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating ops status'));
        }
      }
    }),
    updateUserEsc: builder.mutation({
      query: ({ taskId, origin, userFacingErrors, escalationType }) => ({
        url: 'ops-system/update-user-esc',
        method: 'POST',
        body: { taskId, origin, userFacingErrors, escalationType }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES, TAGS.USER_ESCALATION_ERRORS],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    updateDoNotFile: builder.mutation({
      query: ({ taskId }) => ({
        url: 'ops-system/update-do-not-file',
        method: 'POST',
        body: { taskId, origin: 'Moved to Do Not File via QDeck' }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating stage'));
        }
      }
    }),
    createTaskSubstages: builder.mutation({
      query: ({ taskId, names }) => ({
        url: `ops-system/tasks/${taskId}/substages`,
        method: 'POST',
        body: { names }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error creating task substages'));
        }
      }
    }),
    deleteTaskSubstage: builder.mutation({
      query: ({ taskSubstageId }) => ({
        url: `ops-system/substages/${taskSubstageId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error deleting task substage'));
        }
      }
    }),
    updateExtensionStatus: builder.mutation({
      query: ({ taskId, selected, reviewer }) => ({
        url: 'ops-system/update-extension-status',
        method: 'POST',
        body: { taskId, selected, reviewer }
      }),
      invalidatesTags: [TAGS.TASK]
    }),
    sendInternalComment: builder.mutation({
      query: ({ userId, taskId, authorEmail, authorName, commentBody, commentTags }) => ({
        url: 'ops-system/send-internal-comment',
        method: 'POST',
        body: { userId, taskId, authorEmail, authorName, commentBody, commentTags }
      })
    }),
    getTask: builder.query({
      query: (taskId) => ({
        url: `ops-system/tasks/${taskId}`,
        method: 'GET'
      }),
      providesTags: [TAGS.TASK],
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled;
          // NOTE: This is needed for certain socket actions to work
          dispatch(setCurrentTask(result.data));
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          // If the task isn't found, redirect to the /not-found page
          else if (e?.error?.status === 404) {
            history.push('/not-found');
          }
        }
      }
    }),
    getTaskComments: builder.query({
      query: (taskId) => ({
        url: `ops-system/tasks/${taskId}/comments`,
        method: 'GET'
      }),
      providesTags: [TAGS.TASK_COMMENTS],
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
        }
      }
    }),
    getTaskActivities: builder.query({
      query: (taskId) => ({
        url: `ops-system/tasks/${taskId}/activities`,
        method: 'GET'
      }),
      providesTags: [TAGS.TASK_ACTIVITIES],
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
        }
      }
    }),
    getTaskForms: builder.query({
      query: (taskId) => ({
        url: `ops-system/tasks/${taskId}/forms`,
        method: 'GET'
      }),
      providesTags: [TAGS.TASK_FORMS],
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
        }
      }
    }),
    uploadForms: builder.mutation({
      query: ({ taskId, formData }) => ({
        url: `ops-system/tasks/${taskId}/forms`,
        method: 'POST',
        body: formData
      }),
      invalidatesTags: [TAGS.TASK_FORMS],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
        }
      }
    }),
    getAskLog: builder.query({
      query: (userId) => ({
        url: `ops-system/tasks/${userId}/ask-log`,
        method: 'GET'
      }),
      providesTags: [TAGS.ASK_LOG],
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
        }
      }
    }),
    getUserEscalationErrors: builder.query({
      query: (taskId) => ({
        url: `ops-system/tasks/${taskId}/user-escalation-errors`,
        method: 'GET'
      }),
      providesTags: [TAGS.USER_ESCALATION_ERRORS],
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
        }
      }
    }),
    getTaskDetailAvailableTaxReturns: builder.query({
      query: (userId) => ({
        url: 'ops-system/task-detail-available-tax-returns',
        method: 'GET',
        params: { userId }
      }),
      transformResponse: (response) => response.data || [],
      providesTags: [TAGS.TASK_DETAIL_AVAILABLE_TAX_RETURNS]
    }),
    getEscalationErrors: builder.query({
      query: () => ({
        url: 'ops-system/escalation-errors',
        method: 'GET'
      }),
      transformResponse: (response) => response.data || [],
      providesTags: [TAGS.ESCALATION_ERRORS]
    }),
    getTaskSubstageCounts: builder.query({
      query: ({ taxYear, stages, showAllReturns, filters }) => ({
        url: `ops-system/task-substage-counts?${qs.stringify(
          { taxYear, stages, showAllReturns: showAllReturns ? '1' : '0', searchTerm: filters.search },
          { arrayFormat: 'repeat' }
        )}`,
        method: 'GET'
      }),
      transformResponse: (response) => response.data,
      providesTags: [TAGS.SUBSTAGE_COUNTS]
    }),
    getTaskStageCounts: builder.query({
      query: ({ taxYear, showAllReturns, filters }) => ({
        url: `ops-system/tasks/stage-counts?${qs.stringify(
          {
            taxYear,
            showAllReturns: showAllReturns ? '1' : '0',
            assignee: filters.assignee,
            searchTerm: filters.search
          },
          { arrayFormat: 'repeat' }
        )}`,
        method: 'GET'
      }),
      transformResponse: (response) => response.data,
      providesTags: [TAGS.STAGE_COUNTS]
    }),
    getTaskYearCounts: builder.query({
      query: () => ({
        url: 'ops-system/tasks/year-counts',
        method: 'GET'
      }),
      transformResponse: (response) => response.data,
      providesTags: [TAGS.STAGE_COUNTS]
    }),
    getTasks: builder.query({
      query: ({ year, showAllReturns, order, filters, pageNumber, pageSize = TASKS_PER_PAGE }) => ({
        url: 'ops-system/tasks',
        method: 'GET',
        params: formatGetTasksParams({
          year,
          showAllReturns,
          order,
          filters,
          pageNumber,
          pageSize
        })
      }),
      transformResponse: (response) => response.data,
      providesTags: [TAGS.TASK_LIST],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskListError(e));
        }
      }
    }),
    createTask: builder.mutation({
      query: ({ type, userId, year, stage, ssn }) => ({
        url: 'ops-system/tasks',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: { type, userId, year, stage, ...(SSN_REQUIRED_TYPES.includes(type) ? { ssn } : {}) }
      }),
      invalidatesTags: [TAGS.TASK_LIST],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskListError(e));
        }
      }
    }),
    bulkUpdateTasks: builder.mutation({
      query: ({ selectedTaskIds, unassign, assignee, stage, substages }) => ({
        url: 'ops-system/task-list-bulk-update',
        method: 'POST',
        body: { selectedTaskIds, unassign, assignee, stage, substages }
      }),
      invalidatesTags: [TAGS.TASK_LIST],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskListError(e));
        }
      }
    }),
    updateOpsStatus: builder.mutation({
      query: ({ taskId, opsStatus }) => ({
        url: `ops-system/tasks/${taskId}/ops-status`,
        method: 'PATCH',
        body: { opsStatus }
      }),
      invalidatesTags: [TAGS.TASK, TAGS.TASK_ACTIVITIES],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          dispatch(setModalType(null));
          await queryFulfilled;
        } catch (e) {
          if (e instanceof Error) {
            sentryCaptureException(e);
          }
          dispatch(handleTaskDetailError(e, 'Error updating ops status'));
        }
      }
    })
  })
});

////////////////////////////////////////// HOOKS //////////////////////////////////////////
export const {
  useUpdateAssigneeMutation,
  useUpdateOpsReviewMutation,
  useUpdateConfirmAmountsMutation,
  useUpdateFinalReviewMutation,
  useUpdateFiledWithIrsMutation,
  useUpdateAcceptedByIrsMutation,
  useUpdateNotStartedMutation,
  useUpdateTaxExpertEscMutation,
  useUpdateEngEscMutation,
  useUpdateUserEscMutation,
  useUpdateDoNotFileMutation,
  useCreateTaskSubstagesMutation,
  useDeleteTaskSubstageMutation,
  useUpdateExtensionStatusMutation,
  useSendInternalCommentMutation,
  useGetTaskDetailAvailableTaxReturnsQuery,
  useGetEscalationErrorsQuery,
  useGetTaskSubstageCountsQuery,
  useGetTaskStageCountsQuery,
  useGetTasksQuery,
  useBulkUpdateTasksMutation,
  useCreateTaskMutation,
  useGetTaskYearCountsQuery,
  useGetTaskQuery,
  useGetTaskCommentsQuery,
  useGetTaskActivitiesQuery,
  useGetTaskFormsQuery,
  useUploadFormsMutation,
  useGetAskLogQuery,
  useGetUserEscalationErrorsQuery,
  useUpdateOpsStatusMutation
} = opsSystemApi;
